import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

export interface BreadcrumbItem {
  label: string;
  route?: string;
  isActive?: boolean;
  count?: number;
  clickHandler?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {

  constructor(private router: Router) { }

  /**
   * Get current user role to determine breadcrumb visibility
   */
  getCurrentUserRole(): string {
    try {
      // Check for userProfile in localStorage
      const userProfile = localStorage.getItem('userProfile');
      if (userProfile) {
        const parsedProfile = JSON.parse(userProfile);
        if (parsedProfile && parsedProfile.role) {
          return parsedProfile.role;
        }
        if (parsedProfile && parsedProfile.roleName) {
          return parsedProfile.roleName;
        }
        if (parsedProfile && parsedProfile.systemRole) {
          return parsedProfile.systemRole;
        }
      }

      // Fallback: try other common localStorage keys
      const userRole = localStorage.getItem('userRole') || 
                      localStorage.getItem('currentUserRole') ||
                      localStorage.getItem('role');
      
      return userRole || 'User'; // Default fallback
    } catch (error) {
      console.warn('Error parsing user profile, defaulting to User:', error);
      return 'User';
    }
  }

  /**
   * Determine if Client/Customer breadcrumb should be shown based on user role
   */
  shouldShowClientCustomerBreadcrumb(): boolean {
    const userRole = this.getCurrentUserRole();
    const retailRoles = ['Retail Admin', 'Retail Manager', 'Retail User'];
    return !retailRoles.includes(userRole);
  }

  /**
   * Determine if only Customer breadcrumb should be shown (no Client level)
   * For User, Customer Admin, Client Admin roles
   */
  shouldShowCustomerOnlyBreadcrumb(): boolean {
    const userRole = this.getCurrentUserRole();
    const customerOnlyRoles = ['User', 'Customer Admin', 'Client Admin'];
    return customerOnlyRoles.includes(userRole);
  }
  
  shouldShowLoactionOnlyBreadcrumb(): boolean {
    const userRole = this.getCurrentUserRole();
    const locationOnlyRoles = ['User1', 'Customer Admin1', 'Client Admin1'];
    return locationOnlyRoles.includes(userRole);
  }

  /**
   * Build breadcrumb items for different module contexts
   */
  buildBreadcrumb(context: 'users'| 'clients' | 'customers' | 'locations' | 'devices', data?: any): BreadcrumbItem[] {
    const items: BreadcrumbItem[] = [];
    const showClientCustomer = this.shouldShowClientCustomerBreadcrumb();
    const showCustomerOnly = this.shouldShowCustomerOnlyBreadcrumb();
    const showLoactionOnly = this.shouldShowLoactionOnlyBreadcrumb();

    switch (context) {
       case 'users':
        items.push({
          label: 'Users',
          count: data?.totalUsers || 0,
          isActive: true
        });
        break;

      case 'clients':
        items.push({
          label: 'Clients',
          count: data?.totalClients || 0,
          isActive: true
        });
        break;

      case 'customers':
        // For Super Admin role, show full Client → Customer breadcrumb
        if (showClientCustomer && !showCustomerOnly) {
          items.push({
            label: 'Clients',
            route: '/clients',
            clickHandler: () => this.navigateToClients()
          });
          
          if (data?.clientName) {
            items.push({
              label: `Client: ${data.clientName}`,
              isActive: false
            });
          }
        }
        // For User, Customer Admin, Client Admin - show only Customers
        // For Retail roles - show only Customers (handled by showClientCustomer = false)
        
        items.push({
          label: 'Customers',
          count: data?.totalCustomers || 0,
          isActive: true
        });
        break;

      case 'locations':
        // For Super Admin role, show full Client → Customer → Location breadcrumb
        if (showClientCustomer && !showCustomerOnly) {
          items.push({
            label: 'Clients',
            route: '/clients',
            clickHandler: () => this.navigateToClients()
          });
          
          if (data?.clientName) {
            items.push({
              label: `Client: ${data.clientName}`,
              clickHandler: () => this.navigateToCustomers(data.clientId)
            });
          }
          
          if (data?.customerName) {
            items.push({
              label: `Customer: ${data.customerName}`,
              clickHandler: () => this.navigateToCustomers(data.clientId)
            });
          }
        }
        // For User, Customer Admin, Client Admin - show Customer → Location (without client info)
        else if (showCustomerOnly && data?.customerName) {
          items.push({
            label: `Customer: ${data.customerName}`,
            clickHandler: () => this.navigateToCustomers()
          });
        }
        // For Retail roles - show only Locations (no breadcrumb navigation)
        
        items.push({
          label: 'Locations',
          count: data?.totalLocations || 0,
          isActive: true
        });
        break;

      case 'devices':
        // For Super Admin role, show full Client → Customer → Location → Devices breadcrumb
        if (showClientCustomer && !showCustomerOnly) {
          // Always show Clients at the top level
          items.push({
            label: 'Clients',
            route: '/clients',
            clickHandler: () => this.navigateToClients()
          });

          // Show Client level with name if available
          if (data?.clientName) {
            items.push({
              label: `Client: ${data.clientName}`,
              clickHandler: () => this.navigateToCustomers(data.clientId)
            });
          }

          // Show Customer level with name if available
          if (data?.customerName) {
            items.push({
              label: `Customer: ${data.customerName}`,
              clickHandler: () => this.navigateToCustomers(data.clientId)
            });
          }

          // Show Location level with name if available
          if (data?.locationName) {
            items.push({
              label: `Location: ${data.locationName}`,
              clickHandler: () => this.navigateToLocations(data.customerId)
            });
          }
        }
        // For Customer Admin, Client Admin - show Customer → Location → Devices
        else if (showCustomerOnly && !showLoactionOnly) {
          // Show Customer level with name if available
          if (data?.customerName) {
            items.push({
              label: `Customer: ${data.customerName}`,
              clickHandler: () => this.navigateToCustomers()
            });
          }

          // Show Location level with name if available
          if (data?.locationName) {
            items.push({
              label: `Location: ${data.locationName}`,
              clickHandler: () => this.navigateToLocations(data.customerId)
            });
          }
        }
        // For Location-only roles - show Location → Devices
        else if (showLoactionOnly) {
          if (data?.locationName) {
            items.push({
              label: `Location: ${data.locationName}`,
              clickHandler: () => this.navigateToLocations()
            });
          }
        }

        // Always show Devices at the end
        items.push({
          label: 'Devices',
          count: data?.deviceCount || 0,
          isActive: true
        });
        break;
    }

    return items;
  }

  /**
   * Navigation helpers
   */
  navigateToClients(): void {
    localStorage.removeItem('clientId');
    localStorage.removeItem('clientName');
    localStorage.removeItem('customerId');
    localStorage.removeItem('customerName');
    localStorage.removeItem('iotCustomerId');
    localStorage.removeItem('locationId');
    localStorage.removeItem('locationName');
    this.router.navigate(['/clients']);
  }

  navigateToCustomers(clientId?: string): void {
    if (clientId) {
      localStorage.setItem('clientId', clientId);
    }
    localStorage.removeItem('customerId');
    localStorage.removeItem('customerName');
    localStorage.removeItem('iotCustomerId');
    localStorage.removeItem('locationId');
    localStorage.removeItem('locationName');
    this.router.navigate(['/customers']);
  }

  navigateToLocations(customerId?: string): void {
    if (customerId) {
      localStorage.setItem('customerId', customerId);
    }
    localStorage.removeItem('locationId');
    localStorage.removeItem('locationName');
    // Mark as hierarchical navigation
    sessionStorage.setItem('hierarchicalNavigation', 'true');
    this.router.navigate(['/locations']);
  }

  /**
   * Get stored data from localStorage
   */
  getStoredData() {
    return {
      clientId: localStorage.getItem('clientId') || '',
      clientName: localStorage.getItem('clientName') || '',
      customerId: localStorage.getItem('customerId') || '',
      customerName: localStorage.getItem('customerName') || '',
      iotCustomerId: localStorage.getItem('iotCustomerId') || '',
      locationId: localStorage.getItem('locationId') || '',
      locationName: localStorage.getItem('locationName') || '',
    };
  }
}